import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Send, CheckCircle2 } from "lucide-react";
import confetti from "canvas-confetti";
import { COUNTRIES } from "../lib/countries";
import { analytics } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRYB_COLORS = ["#3b5bff", "#a855f7", "#ec4899", "#22d3ee", "#fbbf24"];

/** Fires a warm, brand-coloured confetti burst around a target element */
const fireConfetti = (targetEl) => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const rect = targetEl?.getBoundingClientRect?.();
  const originX = rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.5;
  const originY = rect ? Math.max((rect.top + rect.height / 2) / window.innerHeight, 0.35) : 0.5;
  const common = { colors: TRYB_COLORS, disableForReducedMotion: true, scalar: 0.95 };
  confetti({ ...common, particleCount: 90, spread: 70, startVelocity: 45, origin: { x: originX, y: originY } });
  // Two side bursts for depth
  setTimeout(() => confetti({ ...common, particleCount: 40, angle: 60, spread: 55, origin: { x: 0.1, y: 0.7 } }), 120);
  setTimeout(() => confetti({ ...common, particleCount: 40, angle: 120, spread: 55, origin: { x: 0.9, y: 0.7 } }), 220);
};

const inputBase =
  "mt-2 w-full rounded-xl bg-foreground/5 border border-foreground/10 px-4 py-3 text-sm focus:outline-none focus:border-foreground/30 transition";
const labelBase =
  "text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground";

const ContactForm = ({ defaultInterest = "all" }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    name: "",
    email: "",
    company: "",
    country: "MA",
    phoneCountryCode: "+212",
    phone: "",
    website: "",
    instagram: "",
    interest: defaultInterest,
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [funnelStarted, setFunnelStarted] = useState(false);

  const update = (k) => (e) => {
    // Fire form_start once, on the first meaningful input
    if (!funnelStarted && e?.target?.value) {
      setFunnelStarted(true);
      analytics.contactFormStart(defaultInterest);
    }
    setState((s) => ({ ...s, [k]: e.target.value }));
  };

  const onFieldFocus = (fieldName) => () => analytics.contactFormFieldFocus(fieldName);

  const onCountryChange = (e) => {
    const code = e.target.value;
    const found = COUNTRIES.find((c) => c.code === code);
    setState((s) => ({
      ...s,
      country: code,
      phoneCountryCode: found?.dial || s.phoneCountryCode,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const submitBtn = e.currentTarget?.querySelector?.("[data-testid=contact-submit]");
      await axios.post(`${API}/contact`, {
        ...state,
        source: "landing",
      });
      setStatus("done");
      // 🎯 Google Ads conversion candidate — GA4 recommended "generate_lead"
      analytics.contactFormSubmit(state.interest, state.country);
      // 🎉 celebratory confetti — brand colours, origin near the submit button
      fireConfetti(submitBtn);
      setState({
        name: "",
        email: "",
        company: "",
        country: "MA",
        phoneCountryCode: "+212",
        phone: "",
        website: "",
        instagram: "",
        interest: defaultInterest,
        message: "",
      });
    } catch (err) {
      setStatus("error");
      const msg = err?.response?.data?.detail?.[0]?.msg || t("contact.error");
      setError(msg);
      analytics.contactFormError(msg);
    }
  };

  if (status === "done") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-strong rounded-2xl p-10 text-center"
        data-testid="contact-success"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
        <p className="font-display mt-5 text-2xl">{t("contact.success")}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("contact.successDetail")}
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="btn-ghost mt-6"
          data-testid="contact-send-another"
        >
          {t("contact.sendAnother")}
        </button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="glass-strong rounded-2xl p-6 md:p-10 space-y-5"
      data-testid={`contact-form-${defaultInterest === "all" ? "main" : defaultInterest}`}
    >
      {/* Name + Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelBase}>{t("contact.name")}</label>
          <input
            required
            value={state.name}
            onChange={update("name")}
            data-testid="contact-input-name"
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase}>{t("contact.email")}</label>
          <input
            required
            type="email"
            value={state.email}
            onChange={update("email")}
            data-testid="contact-input-email"
            className={inputBase}
          />
        </div>
      </div>

      {/* Company + Country */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelBase}>{t("contact.company")}</label>
          <input
            required
            value={state.company}
            onChange={update("company")}
            data-testid="contact-input-company"
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase}>{t("contact.country")}</label>
          <select
            required
            value={state.country}
            onChange={onCountryChange}
            data-testid="contact-input-country"
           
            className={inputBase}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code} className="bg-[#0a0b14] text-white">
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Phone (dial code + number) */}
      <div>
        <label className={labelBase}>{t("contact.phone")}</label>
        <div className="mt-2 grid grid-cols-[7.5rem_1fr] gap-2">
          <select
            required
            value={state.phoneCountryCode}
            onChange={update("phoneCountryCode")}
            data-testid="contact-input-phone-code"
           
            className="rounded-xl bg-foreground/5 border border-foreground/10 px-3 py-3 text-sm focus:outline-none focus:border-foreground/30 transition"
          >
            {COUNTRIES.map((c) => (
              <option key={`${c.code}-${c.dial}`} value={c.dial} className="bg-[#0a0b14] text-white">
                {c.code} {c.dial}
              </option>
            ))}
          </select>
          <input
            required
            type="tel"
            inputMode="tel"
            placeholder="6 12 34 56 78"
            value={state.phone}
            onChange={update("phone")}
            data-testid="contact-input-phone"
            className="rounded-xl bg-foreground/5 border border-foreground/10 px-4 py-3 text-sm focus:outline-none focus:border-foreground/30 transition"
          />
        </div>
      </div>

      {/* Website + Instagram (optional) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelBase}>
            {t("contact.website")}{" "}
            <span className="ml-1 text-[10px] font-medium opacity-60">{t("contact.optional")}</span>
          </label>
          <input
            type="url"
            placeholder="https://"
            value={state.website}
            onChange={update("website")}
            data-testid="contact-input-website"
            className={inputBase}
          />
        </div>
        <div>
          <label className={labelBase}>
            {t("contact.instagram")}{" "}
            <span className="ml-1 text-[10px] font-medium opacity-60">{t("contact.optional")}</span>
          </label>
          <input
            placeholder="@yourhandle"
            value={state.instagram}
            onChange={update("instagram")}
            data-testid="contact-input-instagram"
            className={inputBase}
          />
        </div>
      </div>

      {/* Interest */}
      <div>
        <label className={labelBase}>{t("contact.interest")}</label>
        <select
          required
          value={state.interest}
          onChange={update("interest")}
          data-testid="contact-input-interest"
         
          className={inputBase}
        >
          <option value="all" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.all")}</option>
          <option value="loyalty" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.loyalty")}</option>
          <option value="review" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.review")}</option>
          <option value="connect" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.connect")}</option>
          <option value="page" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.page")}</option>
          <option value="booking" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.booking")}</option>
          <option value="luckywheel" className="bg-[#0a0b14] text-white">{t("contact.interestOptions.luckywheel")}</option>
        </select>
      </div>

      {/* Message */}
      <div>
        <label className={labelBase}>{t("contact.message")}</label>
        <textarea
          required
          rows={5}
          value={state.message}
          onChange={update("message")}
          data-testid="contact-input-message"
          className={`${inputBase} resize-none`}
        />
      </div>

      {status === "error" && (
        <p className="text-sm text-red-400" data-testid="contact-error">{error}</p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="btn-primary w-full disabled:opacity-70"
        data-testid="contact-submit"
      >
        <Send className="h-4 w-4" />
        {status === "sending" ? t("contact.sending") : t("contact.send")}
      </button>
    </form>
  );
};

export default ContactForm;
